import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton, StatusBadge } from "@/components/common/Common";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Building2, Plus, MoreHorizontal, Pencil, Power, Trash2, Download, Search } from "lucide-react";

const empty = { code: "", name: "", location: "", address: "", phone: "", email: "", status: "active" };

export default function BranchesPage() {
  const { t, can } = useApp();
  const [branches, setBranches] = useState(null);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => { try { const { data } = await api.get("/branches"); setBranches(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setDialog(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/branches/${editing.id}`, form); toast.success("Branch updated"); }
      else { await api.post("/branches", form); toast.success("Branch created"); }
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (b) => {
    try { await api.patch(`/branches/${b.id}/status`); toast.success("Status updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async (b) => {
    try { await api.delete(`/branches/${b.id}`); toast.success("Branch deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const exportCsv = () => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/branches/export`, "_blank"); };

  const filtered = (branches || []).filter((b) =>
    !search || `${b.name} ${b.code} ${b.location}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("branchManagement")} subtitle={t("branches")}>
        {can("reports.export") && <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv} data-testid="branches-export-btn"><Download className="h-4 w-4" />{t("export")}</Button>}
        {can("branches.create") && <Button size="sm" className="gap-2" onClick={openCreate} data-testid="create-branch-btn"><Plus className="h-4 w-4" />{t("createBranch")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="branches-search" />
          </div>
        </div>
        {branches === null ? <TableSkeleton /> : filtered.length === 0 ? (
          <EmptyState icon={Building2} title={t("noBranches")}
            action={can("branches.create") && <Button size="sm" className="gap-2" onClick={openCreate}><Plus className="h-4 w-4" />{t("createBranch")}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("branchCode")}</TableHead>
                  <TableHead>{t("branchName")}</TableHead>
                  <TableHead>{t("location")}</TableHead>
                  <TableHead>{t("manager")}</TableHead>
                  <TableHead>{t("users")}</TableHead>
                  <TableHead>{t("status")}</TableHead>
                  <TableHead className="text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                    <TableCell className="font-mono text-xs font-semibold">{b.code}</TableCell>
                    <TableCell className="font-medium">{b.name}</TableCell>
                    <TableCell className="text-muted-foreground">{b.location}</TableCell>
                    <TableCell className="text-muted-foreground">{b.manager_name || "—"}</TableCell>
                    <TableCell>{b.user_count}</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`branch-actions-${b.code}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can("branches.edit") && <DropdownMenuItem onClick={() => openEdit(b)} data-testid={`branch-edit-${b.code}`}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>}
                          {can("branches.disable") && <DropdownMenuItem onClick={() => toggleStatus(b)} data-testid={`branch-toggle-${b.code}`}><Power className="h-4 w-4 mr-2" />{b.status === "active" ? t("disable") : t("enable")}</DropdownMenuItem>}
                          {can("branches.delete") && <DropdownMenuItem className="text-destructive" onClick={() => setConfirm(b)} data-testid={`branch-delete-${b.code}`}><Trash2 className="h-4 w-4 mr-2" />{t("delete")}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? t("editBranch") : t("createBranch")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("branchCode")} *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" data-testid="branch-code-input" /></div>
            <div><Label>{t("branchName")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="branch-name-input" /></div>
            <div><Label>{t("location")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" data-testid="branch-location-input" /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="branch-phone-input" /></div>
            <div className="col-span-2"><Label>{t("address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="branch-address-input" /></div>
            <div><Label>{t("email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="branch-email-input" /></div>
            <div><Label>{t("status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="branch-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">{t("active")}</SelectItem><SelectItem value="inactive">{t("inactive")}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving || !form.code || !form.name} data-testid="branch-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} destructive
        title="Delete branch?" description={`Are you sure you want to delete "${confirm?.name}"? This cannot be undone.`}
        confirmLabel={t("delete")} onConfirm={() => doDelete(confirm)} />
    </div>
  );
}
